import './FiltersBar.less';
import MenuButton, { MenuButtonActions } from '../button/MenuButton';
import Checkbox from '../input/Checkbox';
import Radio from '../input/Radio';
import { PriceRangeSelector } from '../product/PriceRangeSelector';
import { formatCurrency } from '../../services/FormatterService';
import { useCallback, useMemo, useRef } from 'preact/hooks';
import ColorGridPicker from '../input/ColorGridPicker';
import { useTranslation } from '../../hooks/useTranslation';
import Button from '../button/Button';
import {
    toggleFieldFilter,
    transformToGroupedFieldFilters
} from '../../services/FilterService';
import useAppDispatch from '../../hooks/useAppDispatch';
import useAppSelector from '../../hooks/useAppSelector';
import { isFilterableFeatureItem } from '../../services/FeatureService';

const FiltersBar = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const sortMenuButtonActions: MenuButtonActions = useRef(null);
    const mainFeatureItem = useAppSelector(
        state => state.featureState.mainFeatureItem
    );
    const groupedFieldFilters = useMemo(() => {
        if (!mainFeatureItem || !isFilterableFeatureItem(mainFeatureItem)) {
            return [];
        }
        return transformToGroupedFieldFilters(
            mainFeatureItem.availableFieldFilters,
            mainFeatureItem.appliedFieldFilters
        );
    }, [mainFeatureItem]);

    const changeSort = useCallback(
        async (appliedSortBy: 'relevance' | 'price-asc' | 'price-desc') => {
            sortMenuButtonActions.current?.close();
            await dispatch.featureState.applyProductSearchFilters({
                appliedSortBy
            });
        },
        [dispatch, sortMenuButtonActions]
    );

    const toggleSelectedFieldFilterValue = useCallback(
        async (fieldName: string, fieldValue: string) => {
            if (!mainFeatureItem || !isFilterableFeatureItem(mainFeatureItem)) {
                return;
            }
            const { appliedFieldFilters, availableFieldFilters } =
                mainFeatureItem;
            await dispatch.featureState.applyProductSearchFilters({
                appliedFieldFilters: toggleFieldFilter(
                    availableFieldFilters,
                    appliedFieldFilters,
                    fieldName,
                    fieldValue
                )
            });
        },
        [mainFeatureItem, dispatch]
    );

    if (!mainFeatureItem || !isFilterableFeatureItem(mainFeatureItem)) {
        return null;
    }

    const {
        appliedMaxPrice,
        appliedMinPrice,
        appliedCategoryFilters,
        appliedFieldFilters,
        appliedSortBy,
        availableMaxPrice,
        availableMinPrice,
        refinementLoadingStatus,
        availableCategoryFilters
    } = mainFeatureItem;

    const minPriceFormatted =
        typeof appliedMinPrice === 'number'
            ? formatCurrency(appliedMinPrice, 'EUR', 'es')
            : '';
    const maxPriceFormatted =
        typeof appliedMaxPrice === 'number'
            ? formatCurrency(appliedMaxPrice, 'EUR', 'es')
            : '';
    const priceFilterText = minPriceFormatted
        ? maxPriceFormatted
            ? minPriceFormatted === maxPriceFormatted
                ? t('FILTER_PRICE_EXACT', { price: minPriceFormatted })
                : t('FILTER_PRICE_RANGE', {
                      from: minPriceFormatted,
                      to: maxPriceFormatted
                  })
            : t('FILTER_FROM_PRICE', { price: minPriceFormatted })
        : maxPriceFormatted
          ? t('FILTER_TO_PRICE', { price: maxPriceFormatted })
          : null;

    const isSomePanelFilterActive =
        appliedCategoryFilters.length > 0 ||
        appliedFieldFilters.filter(f => !f.isFeaturedFilterInUI).length > 0;

    const resultsQuantity =
        'totalQuantity' in mainFeatureItem
            ? mainFeatureItem.totalQuantity
            : undefined;

    const filterShownInPanel =
        groupedFieldFilters.some(filter => !filter.isFeaturedFilterInUI) ||
        availableCategoryFilters.length > 1;

    return (
        <div className={`bri-FiltersBar`}>
            <div className={`bri-FiltersBar-row`}>
                {filterShownInPanel && (
                    <div className="bri-FiltersBar-filter-button">
                        <Button
                            variant="tertiary"
                            startIconId="tune"
                            startIconSize="medium"
                            size="medium"
                            onClick={() =>
                                dispatch.featureState.setFilterPanelOpen(true)
                            }
                            title={t('ALL_FILTERS')}
                        />
                        {isSomePanelFilterActive && (
                            <div className="bri-FiltersBar-dot" />
                        )}
                    </div>
                )}
                <MenuButton
                    disabled={refinementLoadingStatus === 'loading'}
                    isActive={!!priceFilterText}
                    menuContent={
                        <div style={{ width: '200px', maxWidth: '100%' }}>
                            <PriceRangeSelector
                                availableMaxPrice={availableMaxPrice}
                                availableMinPrice={availableMinPrice}
                                appliedMaxPrice={appliedMaxPrice}
                                appliedMinPrice={appliedMinPrice}
                                onChange={prices =>
                                    dispatch.featureState.applyProductSearchFilters(
                                        prices
                                    )
                                }
                            />
                        </div>
                    }
                >
                    {t('PRICE')}
                    {!!priceFilterText && <span>&nbsp;(1)</span>}
                </MenuButton>
                {groupedFieldFilters
                    .filter(
                        groupedFieldFilter =>
                            groupedFieldFilter.isFeaturedFilterInUI
                    )
                    .map(groupedFieldFilter => (
                        <MenuButton
                            disabled={refinementLoadingStatus === 'loading'}
                            isActive={groupedFieldFilter.fieldValues.some(
                                f => f.isSelected
                            )}
                            className="bri-MenuButton-filter-option"
                            menuContent={
                                groupedFieldFilter.fieldType === 'color' ? (
                                    <div
                                        style={{
                                            width: '275px',
                                            maxWidth: '100%'
                                        }}
                                    >
                                        <ColorGridPicker
                                            availableColors={groupedFieldFilter.fieldValues.map(
                                                fieldValue => ({
                                                    color: fieldValue.color,
                                                    id: fieldValue.fieldValue,
                                                    isSelected:
                                                        fieldValue.isSelected,
                                                    label: fieldValue.fieldTranslatedValue
                                                })
                                            )}
                                            onItemClick={async item => {
                                                const existingFieldValue =
                                                    groupedFieldFilter.fieldValues.find(
                                                        fv =>
                                                            fv.fieldValue ===
                                                            item.id
                                                    );
                                                if (!existingFieldValue) {
                                                    return;
                                                }
                                                await toggleSelectedFieldFilterValue(
                                                    groupedFieldFilter.fieldName,
                                                    existingFieldValue.fieldValue
                                                );
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        {groupedFieldFilter.fieldValues.map(
                                            fieldValue => (
                                                <Checkbox
                                                    checked={
                                                        fieldValue.isSelected
                                                    }
                                                    onChange={() =>
                                                        toggleSelectedFieldFilterValue(
                                                            groupedFieldFilter.fieldName,
                                                            fieldValue.fieldValue
                                                        )
                                                    }
                                                >
                                                    {
                                                        fieldValue.fieldTranslatedValue
                                                    }
                                                </Checkbox>
                                            )
                                        )}
                                    </>
                                )
                            }
                        >
                            {groupedFieldFilter.fieldTranslatedName}
                            {groupedFieldFilter.quantitySelected > 0 && (
                                <span>
                                    &nbsp;(
                                    {groupedFieldFilter.quantitySelected})
                                </span>
                            )}
                        </MenuButton>
                    ))}
                <div className="bri-FiltersBar-quantity bri-Body2Font">
                    {resultsQuantity !== undefined
                        ? resultsQuantity === 1
                            ? t('PRODUCTS_LABEL_ONE')
                            : t('PRODUCTS_LABEL_MANY', {
                                  count: resultsQuantity
                              })
                        : ' '}
                </div>
                <MenuButton
                    disabled={refinementLoadingStatus === 'loading'}
                    actions={sortMenuButtonActions}
                    menuContent={
                        <>
                            <Radio
                                id="relevance"
                                name="sort-by"
                                checked={appliedSortBy === 'relevance'}
                                onChange={() => changeSort('relevance')}
                            >
                                {t('SORT_FEATURED')}
                            </Radio>
                            <Radio
                                id="price-asc"
                                name="sort-by"
                                checked={appliedSortBy === 'price-asc'}
                                onChange={() => changeSort('price-asc')}
                            >
                                {t('SORT_PRICE_ASC')}
                            </Radio>
                            <Radio
                                id="price-desc"
                                name="sort-by"
                                checked={appliedSortBy === 'price-desc'}
                                onChange={() => changeSort('price-desc')}
                            >
                                {t('SORT_PRICE_DESC')}
                            </Radio>
                        </>
                    }
                >
                    {appliedSortBy === 'relevance' && t('SORT_FEATURED')}
                    {appliedSortBy === 'price-asc' && t('SORT_PRICE_ASC')}
                    {appliedSortBy === 'price-desc' && t('SORT_PRICE_DESC')}
                </MenuButton>
            </div>
        </div>
    );
};

export default FiltersBar;
